import { readonly, ref } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type { GetProductSearchParams } from '~/modules/catalog/product/types';
import { MatchEnumSettings } from '~/modules/GraphQL/types';
import useApi from '~/composables/useApi';
import { sortingOptions, SortingOptionsValuesEnum } from '~/modules/catalog/category/composables/useFacet/sortingOptions';
import { perPageOptions } from '~/modules/catalog/category/composables/useFacet/perPageOptions';
import { createProductAttributeFilterInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeFilterInput';
import { createProductAttributeSortInput } from '~/modules/catalog/category/composables/useFacet/input/createProductAttributeSortInput';
import { Products } from '~/modules/GraphQL/types';
import getFacetDataQuery from './getFacetData.gql';
import getSearchDataQuery from './getSearchData.gql';

import type {
  UseFacetInterface, UseFacetErrors, UseFacetSearchResult, FacetSearchParams,
} from './useFacet';

/**
 * Allows searching for products with pagination, totals and sorting options.
 *
 * See the {@link UseFacetInterface} for a list of methods and values available in this composable.
 */
export function useFacet(testingCategories): UseFacetInterface {
  const { query } = useApi();
  const loading = ref(false);
  const result = ref<UseFacetSearchResult>({ data: null, input: null });
  const error = ref<UseFacetErrors>({
    search: null,
  });
  const defaultItemsPerPage = 20;
  const search = async (params?: FacetSearchParams) => {
    Logger.debug('useFacet/search', params);

    result.value.input = params;
    try {
      loading.value = true;

      const pageSize = params.itemsPerPage ? params.itemsPerPage : defaultItemsPerPage;

      const productCategoryParams: GetProductSearchParams = {
        pageSize,
        search: '',
        filter: createProductAttributeFilterInput(params, true),
        sort: createProductAttributeSortInput(params.sort || SortingOptionsValuesEnum.POSITION_ASC),
        currentPage: params.page,
      };

      let categoryIds = params?.ids || [];
      /**
       * IN oreder to test product configuraions, the product must have ONLY the Testing Category. 
       * With this filter we are excluding those products from search results.
       */
      if(testingCategories?.length>0) categoryIds = categoryIds.filter(el => !testingCategories.includes(el));
      const productSearchParams: GetProductSearchParams = {
        pageSize,
        search: params.term,
        currentPage: params.page,
        sort: createProductAttributeSortInput(params.sort || ''),
        filter: { category_id: { in: categoryIds }, ...createProductAttributeFilterInput(params) }
      }
      const getCorrectQuery = params.term ? getSearchDataQuery : getFacetDataQuery;
      const getCorrectParams = params.term ? productSearchParams : productCategoryParams;

      const { data } = await query<{ products: Products }>(getCorrectQuery, getCorrectParams);
      const products = data?.products ?? null;
      Logger.debug('[Result]:', { products });

      result.value.data = {
        items: products?.items ?? [],
        total: products?.total_count,
        availableSortingOptions: sortingOptions,
        perPageOptions,
        itemsPerPage: pageSize,
      };
      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useFacet/search', err);
    } finally {
      loading.value = false;
    }
  };

  return {
    search,
    result,
    error: readonly(error),
    loading: readonly(loading),
  };
}

export * from './useFacet';
export default useFacet;
